// Extracted sph_progressive_noise_5 config singleton (Phase 6). The original AMD
// module's `exports` object doubled as a mutable config bag that other modules
// read as `cconst.X` and that load()/headlessLoad() mutate in place. ESM module
// namespaces are immutable, so the singleton is modeled as one default-exported
// object accessed as `cconst.X`. Only the LAST `defaults = {...}` (there were 4
// successive reassignments) is actually load()ed, so just that one is inlined.
import type * as uiModule from "../ui.js";

type ConfigBag = Record<string, any>;

// NOTE: the original module wrote a distinct point-record layout (PTOT=18) onto
// window, but at runtime the main const.ts PointRecord (PTOT=20) always won the
// globals, so the SPH solver actually operated on the main layout. sph.ts reads
// the main P* globals directly; no strides are defined here.

const config: ConfigBag = {
  gridoffs: [
    [0, 0],
    [-1, -1],
    [-1, 0],
    [-1, 1],

    [0, 1],
    [1, 1],
    [1, 0],

    [1, -1],
    [0, -1],
  ],

  _save_exclude: {
    gridoffs: 1,
    _save_exclude: 1,
  },

  SmoothModes: {
    SIMPLE: 1,
    POLYFIT: 2,
  },

  PATH_DEGREE: 8,

  SPH_CURVE: undefined,
  RADIUS_CURVE: undefined,
  TONE_CURVE: undefined,
  IMAGE_CURVE: undefined,
  APPLY_TONING: false,

  SEARCHRAD: 1.5,
  CMYK_SEARCHRAD: 4,

  DRAW_TEST: false,
  DIMEN: 32,
  MAX_SCALE: 8.0,
  RADMUL: 0.8,
  PATH_SMOOTH_FACTOR: 1.0,
  SIMPLE_MODE: false,
  DISPLAY_LEVEL: 1,
  SOLVE_LEVEL: 1,
  DV_DAMPING: 1.0,
  GENSTART: 0.0,
  PULL_FACTOR: 0.05,
  RANGE: 255,
  REPEAT: 5,
  STARTCO_BLEND: 1.0,
  PRESTEPS: 16,
  RANDFAC: 1.5,
  PARAM1: 0,
  PARAM2: 0,
  PARAM3: 0,
  PARAM4: 0,
  PARAM5: 0,
  PARAM6: 0,
  PARAM7: 0,
  DRAW_INTENSITY: false,
  DRAW_COLORS: true,
  IMAGE_COLORS: true,
  USE_IMAGE: true,
  GENSTEPS: 255,

  SOLVER_PARAM_MIN: 0,
  SOLVER_PARAM_MAX: 7,
  SOLVER_STEPS: 15,
  SOLVER_SPEED: 1.0,
  SOLVER_SEARCHRAD: 2.5,
  SOLVER_RANGE: 24,
  SOLVER_NORMAL_STEPS: 15,
  SOLVER_PARAM_WEIGHT: 1.0,
  SOLVER_SEARCHRAD_WEIGHT: 1.0,
  SOLVER_MAXSCALE_WEIGHT: 1.0,
  SOLVER_RANDFAC: 1.0,
  SOLVER_IN_SERIES: true,
  SOLVER_RANDSEED: 1,
  SOLVER_STARTSPEED_THRESH: 1.0,

  copy(this: ConfigBag): ConfigBag {
    const ret: ConfigBag = {};

    for (const k in this) {
      ret[k] = this[k];
    }

    return ret;
  },

  toJSON(this: ConfigBag): ConfigBag {
    const ret: ConfigBag = {};

    for (const k in this) {
      const v = this[k];

      if (k in (this._save_exclude as Record<string, unknown>)) {
        continue;
      }

      if (typeof v == "function") {
        continue;
      }

      if (k[0] == "_") {
        continue;
      }

      ret[k] = v;
    }

    return ret;
  },

  headlessLoad(cfg: ConfigBag | undefined, uimodule: typeof uiModule): void {
    if (cfg !== undefined) {
      for (const k in cfg) {
        config[k] = cfg[k];
      }
    }

    for (const k in config) {
      const v = config[k];

      if (typeof v == "object" && v && v.is_new_curve) {
        const curve = new uimodule.Curve(v.setting_id);

        curve.loadJSON(v);
        config[k] = curve;
      }
    }
  },

  loadJSON(obj: ConfigBag): void {
    for (const k in obj) {
      if (k in (config._save_exclude as Record<string, unknown>)) {
        continue;
      }

      if (k[0] != "_") {
        config[k] = obj[k];
      }
    }
  },
};

function load(obj: ConfigBag): void {
  for (const k in obj) {
    config[k] = obj[k];
  }
}

// The last of the original's four successive `defaults = {...}` assignments — the
// only one that was load()ed.
const defaults: ConfigBag = {
  SmoothModes: { SIMPLE: 1, POLYFIT: 2 },
  PATH_DEGREE: 8,
  IMAGE_CURVE: {
    is_new_curve: true,
    setting_id: "startup_file_spha5__Image CurveIMAGE_CURVE",
    generators: [
      {
        type: 1,
        points: [
          { 0: 0, 1: 0, eid: 1, flag: 0, deg: 3, tangent: 1 },
          { 0: 0, 1: 0, eid: 1, flag: 0, deg: 3, tangent: 1 },
          { 0: 0.3437499999999999, 1: 0.1812499999999998, eid: 16, flag: 0, deg: 3, tangent: 1 },
          { 0: 0.6312500000000004, 1: 0.8562500000000001, eid: 11, flag: 1, deg: 3, tangent: 1 },
          { 0: 0.9999999999999999, 1: 1, eid: 3, flag: 0, deg: 3, tangent: 1 },
          { 0: 1, 1: 1, eid: 2, flag: 0, deg: 3, tangent: 1 },
        ],
        deg: 6,
        eidgen: { _cur: 17 },
      },
      { type: 2, equation: "x" },
      { type: 4, height: 1, offset: 0, deviation: 3 },
    ],
    version: 0.5,
    active_generator: 0,
  },
  TONE_CURVE: {
    is_new_curve: true,
    setting_id: "startup_file_spha5__Tone CurveTONE_CURVE",
    generators: [
      {
        type: 1,
        points: [
          { 0: 0, 1: 0, eid: 1, flag: 0, deg: 3, tangent: 1 },
          { 0: 0, 1: 0, eid: 1, flag: 0, deg: 3, tangent: 1 },
          { 0: 0.675, 1: 0.1937500000000001, eid: 39, flag: 1, deg: 3, tangent: 1 },
          { 0: 0.825, 1: 0.13124999999999987, eid: 38, flag: 1, deg: 3, tangent: 1 },
          { 0: 0.9812500000000001, 1: 0.99375, eid: 28, flag: 0, deg: 3, tangent: 1 },
        ],
        deg: 6,
        eidgen: { _cur: 40 },
      },
      { type: 2, equation: "x**0.75" },
      { type: 4, height: 1, offset: 0, deviation: 3 },
    ],
    version: 0.5,
    active_generator: 1,
  },
  RADIUS_CURVE: {
    is_new_curve: true,
    setting_id: "startup_file_spha5__Radius CurveRADIUS_CURVE",
    generators: [
      {
        type: 1,
        points: [
          { 0: 0, 1: 0, eid: 1, flag: 0, deg: 3, tangent: 1 },
          { 0: 0, 1: 0, eid: 1, flag: 0, deg: 3, tangent: 1 },
          { 0: 0.006249999999999992, 1: 0.23750000000000004, eid: 16, flag: 0, deg: 3, tangent: 1 },
          { 0: 0.08750000000000008, 1: 0.4562499999999998, eid: 12, flag: 1, deg: 3, tangent: 1 },
          { 0: 0.21874999999999997, 1: 0.7125000000000001, eid: 15, flag: 0, deg: 3, tangent: 1 },
          { 0: 0.475, 1: 0.9187500000000001, eid: 14, flag: 0, deg: 3, tangent: 1 },
          { 0: 0.69375, 1: 1, eid: 13, flag: 0, deg: 3, tangent: 1 },
          { 0: 1, 1: 1, eid: 2, flag: 0, deg: 3, tangent: 1 },
        ],
        deg: 6,
        eidgen: { _cur: 17 },
      },
      { type: 2, equation: "x**0.75" },
      { type: 4, height: 1, offset: 1, deviation: 0.3 },
    ],
    version: 0.5,
    active_generator: 1,
  },
  SPH_CURVE: {
    is_new_curve: true,
    setting_id: "startup_file_spha5__SPH CurveSPH_CURVE",
    generators: [
      {
        type: 1,
        points: [
          { 0: 0, 1: 0, eid: 1, flag: 0, deg: 3, tangent: 1 },
          { 0: 0, 1: 0, eid: 1, flag: 0, deg: 3, tangent: 1 },
          { 0: 0, 1: 0, eid: 6, flag: 0, deg: 3, tangent: 1 },
          { 0: 0.2124999999999999, 1: 0.29375000000000007, eid: 8, flag: 0, deg: 3, tangent: 1 },
          { 0: 0.60625, 1: 0.0687500000000002, eid: 10, flag: 0, deg: 3, tangent: 1 },
          { 0: 0.84375, 1: 0.33125000000000004, eid: 9, flag: 1, deg: 3, tangent: 1 },
          { 0: 1, 1: 1, eid: 2, flag: 0, deg: 3, tangent: 1 },
          { 0: 1, 1: 1, eid: 2, flag: 0, deg: 3, tangent: 1 },
        ],
        deg: 6,
        eidgen: { _cur: 11 },
      },
      { type: 2, equation: "x" },
      { type: 4, height: 1, offset: 1, deviation: 0.2676 },
    ],
    version: 0.5,
    active_generator: 2,
  },
  APPLY_TONING: true,
  SEARCHRAD: 2,
  CMYK_SEARCHRAD: 0.5,
  DRAW_TEST: true,
  DIMEN: 32,
  MAX_SCALE: 2,
  RADMUL: 0.95,
  PATH_SMOOTH_FACTOR: 1,
  SIMPLE_MODE: true,
  DISPLAY_LEVEL: 1,
  SOLVE_LEVEL: 1,
  DV_DAMPING: 1,
  GENSTART: 0.05,
  PULL_FACTOR: 0.05,
  RANGE: 255,
  REPEAT: 20,
  STARTCO_BLEND: 1,
  PRESTEPS: 16,
  RANDFAC: 7.6736,
  PARAM1: 0.2391,
  PARAM2: 0.2391,
  PARAM3: 1.4782000000000002,
  PARAM4: 4.2943,
  PARAM5: 1.1402,
  PARAM6: 1.4782000000000002,
  PARAM7: 0.915,
  DRAW_INTENSITY: false,
  DRAW_COLORS: false,
  IMAGE_COLORS: true,
  USE_IMAGE: false,
  GENSTEPS: 4096,
  SOLVER_PARAM_MIN: 1,
  SOLVER_PARAM_MAX: 4,
  SOLVER_STEPS: 10,
  SOLVER_SPEED: 1,
  SOLVER_SEARCHRAD: 1.3,
  SOLVER_RANGE: 32,
  SOLVER_NORMAL_STEPS: 30,
  SOLVER_PARAM_WEIGHT: 1,
  SOLVER_SEARCHRAD_WEIGHT: 0,
  SOLVER_MAXSCALE_WEIGHT: 0,
  SOLVER_RANDFAC: 1.0322600000000002,
  SOLVER_IN_SERIES: false,
  SOLVER_RANDSEED: 1,
  SOLVER_STARTSPEED_THRESH: 2.425,
  APP_VERSION: 0.0001,
  USE_MASK: false,
  PROG_BASE_LAYER: true,
  EXPONENT: 0.1,
  PREPOWER: 1,
  TONE_IN_SOLVER: false,
  KEEP_UNIFORM: false,
  RMUL: 1,
  SPH_SPEED: 0.225,
  DISPLAY_LEVEL1: 0,
  DISPLAY_LEVEL2: 1,
  POINTSCALE: 0.293,
  PARAM: 0.45,
  TIMER_STEPS: 33554432,
  START_THRESHOLD: 0.3,
  SMALL_MASK: false,
  XLARGE_MASK: false,
  SHOW_RADII: false,
  VOIDCLUSTER: true,
  ALTERNATE: false,
  GUASS_MIN: 0.1,
  GUASS_POW: 1,
  SCALE_GAUSS: false,
  SCALE_RADIUS: false,
  RADIUS_POW: 1,
  PROPEGATE_W: true,
  INITIAL_W_POWER: 4,
  DRAW_KDTREE: false,
  TONE_MASK: true,
  SEARCHRAD2: 3.96,
  EXP2: 0.8,
  Equations: { W5: 0, W4: 1, W3: 2, GUASSIAN: 3, CUBIC: 4, POW: 5 },
  W_EQUATION: 3,
  SPH_EQUATION: 0,
  W_PARAM: 0.6,
  W_PARAM2: 0.4,
  SPH_PARAM: 1,
  SHOW_PATHS: false,
  UPDATE_START_COS: false,
  ADV_SOLVE: false,
  ADV_STEPS: 32,
  CMYRK_SEARCHRAD: 4,
  CURVE_DEFAULTS: {
    TONE_CURVE: {
      points: [
        { 0: 0.13125, 1: 0, eid: 1, flag: 0, deg: 3, tangent: 1 },
        { 0: 0.26249999999999996, 1: 0.6, eid: 5, flag: 1, deg: 3, tangent: 1 },
        { 0: 1, 1: 1, eid: 2, flag: 0, deg: 3, tangent: 1 },
        { 0: 1, 1: 1, eid: 3, flag: 0, deg: 3, tangent: 1 },
      ],
      eidgen: { _cur: 9 },
    },
  },
  PARAM0: 0.7865952966818254,
};

load(defaults);

export default config;
